<template>
  <v-container>
    <v-alert
      v-model="noIcnWarning"
      dismissable
      prominent
      type="warning"
      transition="scale-transition"
    >
      This subclient does not have an ICN abbreviation, and this
      item cannot be received without one.
      <br>
      <v-btn
        color="primary darken-1"
        class="text-center"
        @click="updateClientDialog = true"
      >
        Update Subclient
      </v-btn>
    </v-alert>
    <v-dialog
      v-model="updateClientDialog"
      max-width="500px"
    >
      <new-client-form
        :initial-data="item.subClient"
        @clientCreated="refreshSubClient"
      />
    </v-dialog>
    <v-dialog
      v-model="confirmApprovalDialog"
      height="500px"
      width="500px"
    >
      <v-card>
        <v-card-title>
          <v-alert
            dense
            type="warning"
          >
            <p>
              <strong>You are about to create an ICN!</strong>
            </p>
            <p>
              Please confirm that the date, notes, physical dimensions,
              additional materials and services, and photos are correct before proceeding.
            </p>
          </v-alert>
        </v-card-title>
        <v-card-actions>
          <v-btn
            text
            @click="confirmApprovalDialog=false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="convertItem()"
          >
            Confirm ICN Creation
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="deleteDialog"
      height="500px"
      width="500px"
    >
      <v-card>
        <v-alert
          dense
          type="error"
        >
          <p class="text-center">
            Are you sure you want to permanently delete this pending inventory item?
            This action cannot be undone.
          </p>
        </v-alert>
        <v-card-actions>
          <v-btn
            text
            @click="deleteDialog=false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="deleteItem()"
          >
            Delete Item
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-card
      v-if="!loading"
      class="elevation-0"
    >
      <v-row>
        <v-row class="ml-2">
          <v-col>
            <span>
              <h1>
                Pending Inventory
              </h1>
            </span>
          </v-col>
          <v-spacer />
          <v-col
            class="mr-2 text-right"
          >
            <v-menu
              top
              origin="center center"
              transition="scale-transition"
            >
              <template v-slot:activator="{ on, attrs} ">
                <v-btn
                  text
                  color="primary"
                  dark
                  v-bind="attrs"
                  v-on="on"
                >
                  Actions
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  @click="toggleEdit()"
                >
                  {{ editing ? 'Cancel Edit' : 'Enable Edit' }}
                </v-list-item>
                <v-divider />
                <v-list-item
                  @click="deleteDialog = true"
                >
                  Delete
                </v-list-item>
                <v-divider />
                <v-list-item
                  :disabled="checkSubClientIcnAbbreviation() || !warehouse"
                  @click="confirmApprovalDialog = true"
                >
                  Receive
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>
      </v-row>
      <v-row>
        <v-col class="col-md-6 elevation-10">
          <v-row class="ml-2">
            <v-col class="col-md-8">
              <v-menu
                ref="menu"
                v-model="menu"
                :close-on-content-click="false"
                :return-value.sync="date"
                max-width="290px"
                transition="slide-x-transition"
                :disabled="!editing"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="date"
                    v-bind="attrs"
                    label="Received Date"
                    v-on="on"
                  >
                    <template v-slot:append>
                      <v-icon
                        :key="`icon-edit-date-${editingDate}`"
                        :color="editingDate ? 'success' : 'danger'"
                        :disabled="!editing"
                        @click="toggleDateEdit()"
                        v-text="editingDate ? 'mdi-lock-open' : 'mdi-lock'"
                      />
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="date"
                  scrollable
                  width="290"
                  :max="new Date().toISOString().substr(0, 10)"
                  :readonly="!editing"
                >
                  <v-spacer />
                  <v-btn
                    color="primary"
                    @click="$refs.menu.save(date)"
                  >
                    OK
                  </v-btn>
                  <v-btn
                    text
                    @click="menu = false"
                  >
                    Cancel
                  </v-btn>
                </v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
          <v-row class="ml-2">
            <v-col class="col-md-8">
              <client-auto-complete
                :client="item.client"
                :locked="clientAutoCompleteLocked"
                :disable-editing="!editing"
                @editingEnabled="showWarning"
                @onClickClient="updateClient"
              />
            </v-col>
          </v-row>
          <v-row class="ml-2">
            <v-col class="col-md-8">
              <client-auto-complete
                :key="rerenderSubClientAutoComplete"
                :client="item.subClient"
                :subclient="true"
                :locked="subClientAutoCompleteLocked"
                :disable-editing="!editing"
                @editingEnabled="showWarning"
                @onClickClient="updateSubClient"
              />
            </v-col>
          </v-row>
          <v-row class="ml-2">
            <v-col class="col-md-8">
              <v-textarea
                v-model="item.description"
                rows="1"
                auto-grow
                :readonly="!editing"
                :rules="[rules.required, rules.lengthSixtyFour]"
                label="Description"
              />
            </v-col>
          </v-row>
          <v-row class="ml-2">
            <v-col class="col-md-8">
              <manufacturer-auto-complete
                :mfg="item.manufacturer"
                :disable-editing="!editing"
                :locked="manufacturerAutoCompleteLocked"
                @onClickMfg="updateMfg"
              />
            </v-col>
          </v-row>
          <v-row class="ml-2">
            <v-col class="col-md-8">
              <warehouse-select-component
                :warehouse="warehouse"
                @onSetModel="warehouseSelected"
              />
            </v-col>
          </v-row>
          <v-row class="ml-2">
            <v-col class="col-md-8">
              <warehouse-location-select-component
                :key="locationSelectorKey"
                :warehouse-id="warehouse ? warehouse.warehouseId : null"
                :location="warehouseLocation"
                :default-pending="true"
                @onSetModel="locationSelected"
              />
            </v-col>
          </v-row>
          <v-row class="ml-2">
            <v-col class="col-sm-2">
              <v-text-field
                v-model.number="item.length"
                label="Length (in inches)"
                type="number"
                :readonly="!editing"
              />
            </v-col>
            <v-col class="col-sm-2">
              <v-text-field
                v-model.number="item.width"
                label="Width (in inches)"
                type="number"
                :readonly="!editing"
              />
            </v-col>
            <v-col class="col-sm-2">
              <v-text-field
                v-model.number="item.height"
                label="Height (in inches)"
                type="number"
                :readonly="!editing"
              />
            </v-col>
            <v-col class="col-sm-2">
              <v-text-field
                v-model.number="cubicFeet"
                label="Cubic Feet"
                readonly
              />
            </v-col>
          </v-row>
          <v-row class="ml-2">
            <v-col class="col-md-8">
              <v-select
                v-model="item.additionalServices"
                :items="servicesOptions"
                :readonly="!editing"
                item-text="title"
                item-value="value"
                label="Additional Services"
              />
            </v-col>
          </v-row>
          <v-row class="ml-2">
            <v-col class="col-md-8">
              <v-textarea
                v-model="item.additionalServicesNotes"
                rows="1"
                auto-grow
                :readonly="!editing"
                label="Additional Services Notes"
              />
            </v-col>
          </v-row>
          <v-row class="ml-2">
            <v-col class="col-md-8">
              <v-combobox
                v-model="item.additionalMaterials"
                :items="materialsOptions"
                :readonly="!editing"
                label="Additional Materials"
              />
            </v-col>
          </v-row>
          <v-row class="ml-2">
            <v-col class="col-md-8">
              <v-textarea
                v-model="item.additionalMaterialsNotes"
                rows="1"
                auto-grow
                :readonly="!editing"
                label="Additional Materials Notes"
              />
            </v-col>
          </v-row>
          <v-row class="ml-2">
            <v-col class="col-md-8">
              <v-textarea
                v-model="item.generalNotes"
                rows="1"
                auto-grow
                :readonly="!editing"
                label="General Notes"
                :rules="[rules.lengthTwoFifty]"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col class="col-md-6">
          <photo-component
            :owner-id="itemId"
            :edit-enabled="!editing"
          />
        </v-col>
      </v-row>
    </v-card>
    <div
      v-else
      class="text-center"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex';
import ClientAutoComplete from './ClientAutoComplete.vue';
import ManufacturerAutoComplete from './ManufacturerAutoComplete.vue';
import PhotoComponent from './PhotoComponent.vue';
import NewClientForm from './NewClientForm.vue';
import WarehouseSelectComponent from './WarehouseComponent.vue';
import WarehouseLocationSelectComponent from './WarehouseLocationComponent.vue';

export default {
  name: 'PendingInventorySummaryForm',
  components: {
    ClientAutoComplete,
    ManufacturerAutoComplete,
    PhotoComponent,
    NewClientForm,
    WarehouseSelectComponent,
    WarehouseLocationSelectComponent,
  },
  props: {
    itemId: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    updateClientDialog: false,
    noIcnWarning: false,
    rerenderSubClientAutoComplete: 0,
    assets: [],
    confirmApprovalDialog: false,
    editing: false,
    editingDate: false,
    newFiles: [],
    newFilesToAdd: [],
    uploadingImages: false,
    deletingImages: false,
    deleteDialog: false,
    uploadedCount: 0,
    selected: [],
    index: 0,
    menu: false,
    date: null,
    loading: true,
    warehouse: null,
    warehouseLocation: null,
    locationSelectorKey: 0,
    visible: false,
    clientAutCompleteLocked: true,
    subClientAutoCompleteLocked: true,
    manufacturerAutCompleteLocked: true,
    item: {
      client: {
        name: '',
      },
      subClient: {
        name: '',
      },
      manufacturer: {
        name: '',
      },
      length: 0.0,
      width: 0.0,
      height: 0.0,
      description: '',
      generalNotes: '',
    },
    servicesOptions: [
      { title: 'n/a', value: 0 },
      { title: '1/4 Hour ($24.75)', value: 24.75 },
      { title: '1/2 Hour ($49.50)', value: 49.50 },
      { title: '3/4 Hour ($74.25)', value: 74.25 },
      { title: '1 Hour ($99.00)', value: 99.00 },
    ],
    materialsOptions: [5.00, 9.00, 14.00, 19.00, 24.00],
    rules: {
      required: (value) => !!value || 'Required',
      lengthSixtyFour: (value) => {
        if (value !== null && value !== undefined && value.length > 64) {
          return 'Max 64 characters';
        }

        return true;
      },
      lengthTwoFifty: (value) => {
        if (value !== null && value !== undefined && value.length > 250) {
          return 'Max 250 characters';
        }

        return true;
      },
    },
  }),
  computed: {
    dateFormatted() {
      return new Date(this.date).toISOString().substr(0, 10);
    },
    cubicFeet() {
      const exactCubicFeet = (this.item.length / 12)
      * (this.item.width / 12)
      * (this.item.height / 12);
      const roundedCubicFeet = (Math.round(exactCubicFeet * 4) / 4).toFixed(2);
      return roundedCubicFeet;
    },
    chargesBeginDate() {
      const dt = new Date(this.date);
      dt.setDate(dt.getDate() + 7);
      return new Date(dt).toISOString().substr(0, 10);
    },
    dailyCharge() {
      return this.chargesToDate.minimumWeeklyCharge
        ? this.$currency(1.00) : this.$currency(this.cubicFeet * 0.04);
    },
    totalCharge() {
      return this.$currency(this.chargesToDate.totalCharge);
    },
  },
  created() {
    this.item = this.getItem();
  },
  methods: {
    ...mapActions('apiClient', ['get', 'del', 'update']),
    showImage(index) {
      this.index = index;
      this.visible = true;
    },
    checkSubClientIcnAbbreviation() {
      return this.item.subClient.icnAbbreviation === null
        || this.item.subClient.icnAbbreviation === undefined
        || this.item.subClient.icnAbbreviation === '';
    },
    toggleEdit() {
      this.editing = !this.editing;

      if (!this.editing) {
        this.clientAutCompleteLocked = true;
        this.subClientAutoCompleteLocked = true;
        this.manufacturerAutCompleteLocked = true;
      }
    },
    toggleDateEdit() {
      this.editingDate = !this.editingDate;

      if (this.editingDate) {
        this.showWarning();
      }
    },
    convertToRoundedFeet(val) {
      return (Math.round((val / 12) * 4) / 4).toFixed(2);
    },
    handleHide() {
      this.visible = false;
    },
    updateClient(client) {
      this.item.client = client;
    },
    updateSubClient(subClient) {
      this.item.subClient = subClient;
    },
    updateMfg(mfg) {
      this.item.manufacturer = mfg;
    },
    warehouseSelected(warehouse) {
      const previousWarehouseId = this.warehouse ? this.warehouse.warehouseId : null;
      this.warehouse = warehouse;
      if (!warehouse || warehouse.warehouseId !== previousWarehouseId) {
        this.warehouseLocation = null;
        this.locationSelectorKey += 1;
      }
    },
    locationSelected(location) {
      this.warehouseLocation = location;
    },
    refreshSubClient(subClient) {
      this.item.subClient = subClient;
      this.noIcnWarning = this.checkSubClientIcnAbbreviation();
      this.rerenderSubClientAutoComplete += 1;
      this.updateClientDialog = false;
    },
    showWarning() {
      this.warnDialog = true;

      setTimeout(() => { this.warnDialog = false; }, 10000);
    },
    showSelectedImage(idx) {
      this.index = idx;
      this.visible = true;
    },
    getAsset(assetId) {
      const assetUrl = `${process.env.VUE_APP_API_BASE_URL}/assets/${assetId}`;
      return assetUrl;
    },
    async uploadImages() {
      this.uploadingImages = true;

      await this.newFiles.reduce(async (memo, file) => {
        await memo;
        const formData = new FormData();
        formData.append('uploadFile', file, file.name);

        await this.create({ url: `/assets/${this.item.itemId}`, body: formData }).then(
          (response) => {
            const asset = {
              src: this.getAsset(response.data.assetId),
              id: response.data.assetId,
            };
            this.assets.push(asset);
            this.newFiles.pop(file);
          },
          (error) => {
            console.log(error);
          },
        );

        const newUploadedCount = this.uploadedCount + 1;
        console.log(`New uploaded count: ${newUploadedCount}`);
        this.uploadedCount = newUploadedCount;
      }, undefined);

      this.uploadingImages = false;
      this.newFiles = [];
    },
    async getItem() {
      this.loading = true;

      await this.get({ url: '/inventory/pending', id: this.itemId }).then(
        (response) => {
          this.item = response.data;
          this.date = new Date(this.item.receivedDate).toISOString().substr(0, 10);
          this.noIcnWarning = this.checkSubClientIcnAbbreviation();
          this.getAssets(response.data.itemId);
        },
        (error) => {
          console.log(error);
        },
      );

      this.loading = false;
    },
    async deleteItem() {
      await this.del({ url: `/inventory/pending/${this.item.itemId}` }).then(
        () => {
          this.deleteDialog = true;

          this.$router.push({ name: 'pendingInventoryList' });
        },
        (error) => {
          console.log(error);
        },
      );
    },
    async getAssets(itemId) {
      await this.get({ url: `/assets/item/${itemId}` }).then(
        (response) => {
          if (response.data.length > 0) {
            response.data.forEach((element) => {
              const assetUrl = `${process.env.VUE_APP_API_BASE_URL}/assets/${element}`;
              const a = { src: assetUrl, id: element };
              this.assets.push(a);
            });
          }
        },
        (error) => {
          console.log(error);
        },
      );
    },
    async convertItem() {
      this.item.receivedDate = new Date(this.date);
      await this.update({ url: '/inventory/pending', body: this.item }).then(
        (response) => {
          this.item = response.data;
        },
      );

      const locationQuery = this.warehouseLocation
        ? `&locationId=${encodeURIComponent(this.warehouseLocation.locationId)}`
        : '';
      const url = `/inventory/convertPending/${this.item.itemId}`
        + `?warehouseId=${encodeURIComponent(this.warehouse.warehouseId)}${locationQuery}`;
      await this.get({ url }).then(
        () => {
          this.$router.push({ name: 'inventoryEdit', params: { itemId: this.item.itemId } });
        },
        (error) => {
          console.log(error);
        },
      );
    },
    addFiles(files) {
      files.forEach((f) => this.newFilesToAdd.push(f));
      this.newFiles = this.newFilesToAdd;
    },
  },
};
</script>
